import posthog, {Properties} from 'posthog-js'
import {PostHogProvider} from 'posthog-js/react'

import {FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef} from 'react'
import {debugLogger} from '../utils/debugLogger'
import {ITrackingContext, TrackingContext} from './Tracking.context'
import {EPlatform, IEvent, IScreen} from './Tracking.types'

// https://posthog.com/tutorials/cookieless-tracking

interface ITrackingProviderProps extends PropsWithChildren {
  applicationName: string
  platform: EPlatform
  posthogToken: string
}

const DEBUG_TAG = 'Tracking'

export const TrackingProvider: FC<ITrackingProviderProps> = ({
  children,
  applicationName,
  platform,
  posthogToken,
}) => {
  const distinctID = useRef<string>(
    Boolean(window.crypto?.randomUUID)
      ? `${window.crypto.randomUUID()}-${Date.now().toString()}`
      : Date.now().toString(),
  )

  const isConfigured = useCallback(() => {
    if (!applicationName || !platform || !distinctID.current) {
      return false
    }

    return true
  }, [applicationName, platform])

  /**
   * Event tracking function
   */
  const trackEvent = useCallback(
    (eventData: IEvent) => {
      if (!isConfigured()) {
        debugLogger({
          message: 'Tracking not configured',
          tag: DEBUG_TAG,
        })
        return
      }

      const eventName = `${eventData.domain}|${eventData.source}|${eventData.trigger}`.toLowerCase()

      debugLogger({
        message: `trackEvent: ${eventName}`,
        tag: DEBUG_TAG,
        data: eventData,
      })

      const {meta} = eventData

      // Posthog
      if (posthogToken) {
        posthog?.capture(eventName, {
          ...meta,
          applicationName: applicationName,
          platform: platform,
          distinctID: distinctID.current,
        })
      }
    },
    [isConfigured, posthogToken, applicationName, platform],
  )

  /**
   * Screen tracking function
   */
  const trackScreenEnter = useCallback(
    (screenData: IScreen) => {
      if (!isConfigured()) {
        debugLogger({
          message: 'Tracking not configured',
          tag: DEBUG_TAG,
        })
        return
      }

      debugLogger({
        message: `trackScreenEnter: ${screenData.screenIdentifier}`,
        tag: DEBUG_TAG,
        data: screenData,
      })

      // Posthog
      if (posthogToken) {
        posthog?.capture('$pageview', {
          pagename: screenData.screenIdentifier,
          $current_url: screenData.abstractPath,
          applicationName: applicationName,
          platform: platform,
          distinctID: distinctID.current,
        })
      }
    },
    [isConfigured, posthogToken, applicationName],
  )

  /**
   * Error tracking function
   */
  const trackError = useCallback(
    (error: Error, additional?: Properties) => {
      if (!isConfigured()) {
        debugLogger({
          message: 'Tracking not configured',
          tag: DEBUG_TAG,
        })
        return
      }

      debugLogger({
        message: `trackError`,
        tag: DEBUG_TAG,
        data: error,
      })

      // Posthog
      if (posthogToken) {
        posthog.captureException(error, {
          ...additional,
          applicationName: applicationName,
          platform: platform,
          distinctID: distinctID.current,
        })
      }
    },
    [isConfigured, posthogToken, applicationName],
  )

  /**
   * Page leave event
   */
  useEffect(() => {
    return () => {
      if (posthogToken) {
        debugLogger({
          message: 'Page leave event',
          tag: DEBUG_TAG,
        })
        posthog?.capture('$pageleave', {
          distinctID: distinctID.current,
        })
      }
      posthog?.reset()
    }
  }, [posthogToken])

  /**
   * Context values
   */

  const TrackingContextValue: ITrackingContext = useMemo(
    () => ({
      trackEvent,
      trackScreenEnter,
      trackError,
    }),
    [trackEvent, trackScreenEnter, trackError],
  )

  if (!posthogToken) {
    return <>{children}</>
  }

  return (
    <PostHogProvider
      apiKey={posthogToken}
      options={{
        autocapture: false,
        persistence: 'memory',
        disable_persistence: true,
        api_host: 'https://eu.i.posthog.com',
        bootstrap: {
          distinctID: distinctID.current,
        },
      }}
    >
      <TrackingContext.Provider value={TrackingContextValue}>{children}</TrackingContext.Provider>
    </PostHogProvider>
  )
}
